/**
 * Cart Visual Sync
 * Asegura que todas las actualizaciones del carrito se reflejen visualmente de forma inmediata
 */

class CartVisualSync {
  constructor() {
    this.initEventListeners();
    this.applyBoxGroupImages();
  }

  getDisplayCount(cartData) {
    if (!cartData || !Array.isArray(cartData.items)) {
      return Number(cartData?.item_count || 0);
    }

    const seenBoxIds = new Set();

    return cartData.items.reduce((total, item) => {
      const boxId = item?.properties?._b || item?.properties?._box_id;

      if (boxId) {
        if (seenBoxIds.has(boxId)) return total;
        seenBoxIds.add(boxId);
        return total + 1;
      }

      return total + Number(item.quantity || 0);
    }, 0);
  }

  initEventListeners() {
    // Escuchar eventos de actualización del carrito
    document.addEventListener('cart:updated', this.handleCartUpdate.bind(this));
    document.addEventListener('cart:itemAdded', this.handleItemAdded.bind(this));
    document.addEventListener('boxbuilder:images-updated', this.handleBoxImagesUpdated.bind(this));

    // Escuchar el evento pub/sub del carrito
    if (window.subscribe && window.PUB_SUB_EVENTS) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, this.handlePubSubCartUpdate.bind(this));
    }
  }

  handleCartUpdate(event) {
    // Forzar actualización visual de todos los elementos relacionados con el carrito
    this.forceVisualUpdate();
  }

  handleItemAdded(event) {
    const cartData = event.detail.cartData;

    // Actualizar inmediatamente todos los contadores
    this.updateAllCartCounters(cartData);
    this.applyBoxGroupImages();

    // Mostrar feedback visual
    this.showAddToCartFeedback();
  }

  handlePubSubCartUpdate(event) {
    if (event.cartData && event.cartData.item_count !== undefined) {
      this.updateAllCartCounters(event.cartData);
    }

    // Pequeño delay para asegurar que el DOM se actualice
    setTimeout(() => {
      this.forceVisualUpdate();
      this.applyBoxGroupImages();
    }, 100);
  }

  handleBoxImagesUpdated() {
    this.applyBoxGroupImages();
    setTimeout(() => this.applyBoxGroupImages(), 120);
  }

  getBoxImageStorage() {
    try {
      const parsed = JSON.parse(localStorage.getItem('box_builder_images') || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  applyBoxGroupImages() {
    const imageMap = this.getBoxImageStorage();
    if (!imageMap || Object.keys(imageMap).length === 0) return;

    const imageTargets = document.querySelectorAll('[data-box-image-target][data-box-id]');

    imageTargets.forEach((imageEl) => {
      const boxId = imageEl.getAttribute('data-box-id');
      const imageUrl = boxId ? imageMap[boxId] : null;

      if (!imageUrl) return;

      imageEl.setAttribute('src', imageUrl);
      imageEl.removeAttribute('hidden');

      const mediaWrapper = imageEl.closest('.box-group-card__media');
      const placeholder = mediaWrapper?.querySelector('[data-box-image-placeholder]');
      if (placeholder) {
        placeholder.style.display = 'none';
      }
    });
  }

  updateAllCartCounters(cartDataOrCount) {
    const itemCount = typeof cartDataOrCount === 'number' ? cartDataOrCount : this.getDisplayCount(cartDataOrCount);

    // Actualizar contador principal del carrito
    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (cartIconBubble) {
      const countElements = cartIconBubble.querySelectorAll(
        '.cart-count-bubble span, .cart-count-bubble-text, [data-cart-count]'
      );
      countElements.forEach((element) => {
        element.textContent = itemCount || '0';
      });

      // Mostrar/ocultar bubble
      const bubble = cartIconBubble.querySelector('.cart-count-bubble');
      if (bubble) {
        if (itemCount === 0) {
          bubble.style.display = 'none';
        } else {
          bubble.style.display = 'flex';
        }
      }
    }

    // Actualizar todos los elementos con data-cart-count
    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    cartCountElements.forEach((element) => {
      element.textContent = itemCount || '0';
    });

    // Actualizar elementos con clases específicas de contador
    const cartCounters = document.querySelectorAll('.cart-count, .cart-counter, .header__cart-count');
    cartCounters.forEach((element) => {
      element.textContent = itemCount || '0';
    });
  }

  showAddToCartFeedback() {
    // Añadir clase temporal para mostrar feedback visual
    const cartIcon = document.querySelector('#cart-icon-bubble, .cart-icon, .header__cart');
    if (cartIcon) {
      cartIcon.classList.add('cart-updated');

      // Remover la clase después de la animación
      setTimeout(() => {
        cartIcon.classList.remove('cart-updated');
      }, 1000);
    }
  }

  forceVisualUpdate() {
    // Forzar reflow de elementos críticos del carrito
    const cartElements = document.querySelectorAll('cart-items, cart-drawer-items, .cart-icon, #cart-icon-bubble');
    cartElements.forEach((element) => {
      if (element) {
        element.offsetHeight; // Forzar reflow
      }
    });

    // Disparar evento de resize para actualizar layouts responsivos
    window.dispatchEvent(new Event('resize'));

    this.applyBoxGroupImages();
  }
}

// Inicializar cuando el DOM esté listo
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartVisualSync();
  });
} else {
  new CartVisualSync();
}
